import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { DateTime } from 'luxon'
import { FlaskConical, Power, QrCode } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import { Badge } from '~/components/ui/badge'
import { Button } from '~/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '~/components/ui/card'
import { Skeleton } from '~/components/ui/skeleton'
import { api } from '~/lib/tuyau'
import { usePermissionGuard, withRoutePermissions, type PermissionRequirement } from '~/utils/rbac'
import { RbacAccessDenied } from '~/components/routes/administration/roles/access-denied'
import { getApiErrorMessage } from '~/lib/utils'

/** Écran 32 — Outils staging. Ressource sans boutique : aucun périmètre ne s'y applique. */
const STAGING_TOOLS_READ: PermissionRequirement = [
  'read',
  'staging-tools.staging-tools.test-account',
]
const STAGING_TOOLS_EDIT: PermissionRequirement = [
  'edit',
  'staging-tools.staging-tools.test-account',
]

const StagingToolsPage = () => {
  const queryClient = useQueryClient()
  const accountQueryOptions = api.stagingTools.getTestAccount.queryOptions()

  // Deux tuples : voir le compte de test, puis l'activer ou lui générer un QR code.
  const canRead = usePermissionGuard(STAGING_TOOLS_READ).authorized
  const canEdit = usePermissionGuard(STAGING_TOOLS_EDIT).authorized

  const {
    data: account,
    isPending,
    isError,
    error,
  } = useQuery({ ...accountQueryOptions, enabled: canRead })

  const [qrcode, setQrcode] = useState<{ image: string; expiresAt: string | null } | null>(null)

  const invalidateAccount = () =>
    queryClient.invalidateQueries({ queryKey: accountQueryOptions.queryKey })

  const { mutate: toggleAccount, isPending: isToggling } = useMutation(
    api.stagingTools.toggleTestAccount.mutationOptions({
      onError: (mutationError) => toast.error(getApiErrorMessage(mutationError)),
      onSuccess: (data) => {
        toast.success(data.active ? 'Compte test activé' : 'Compte test désactivé')
        if (!data.active) setQrcode(null)
        invalidateAccount()
      },
    }),
  )

  const { mutate: generateQrcode, isPending: isGenerating } = useMutation(
    api.stagingTools.generateTestAccountQrcode.mutationOptions({
      onError: (mutationError) => toast.error(getApiErrorMessage(mutationError)),
      onSuccess: (data) => setQrcode({ image: data.image, expiresAt: data.expiresAt }),
    }),
  )

  if (!canRead) return <RbacAccessDenied />

  return (
    <div className="p-6 flex flex-col gap-4 max-w-2xl">
      <div className="flex items-center gap-2">
        <FlaskConical className="size-6" />
        <h1 className="text-2xl font-semibold">Outils staging</h1>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Compte test</CardTitle>
          <CardDescription>
            Active/désactive le compte test et génère des QR codes valables 30 minutes pour ouvrir
            la porte en mode « account ».
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          {isPending && <Skeleton className="h-16 w-full" />}

          {isError && (
            <p className="text-sm text-muted-foreground">
              Compte test indisponible : {getApiErrorMessage(error)}
            </p>
          )}

          {account && (
            <>
              <div className="flex items-center justify-between">
                <div className="flex flex-col">
                  <span className="font-medium">
                    {account.firstName} {account.lastName}
                  </span>
                  <span className="text-sm text-muted-foreground">{account.email}</span>
                </div>
                <Badge variant={account.active ? 'default' : 'destructive'}>
                  {account.active ? 'Actif' : 'Désactivé'}
                </Badge>
              </div>

              <div className="flex gap-2">
                <Button
                  variant={account.active ? 'destructive' : 'default'}
                  disabled={isToggling || !canEdit}
                  onClick={() => toggleAccount({})}
                >
                  <Power className="size-4" />
                  {account.active ? 'Désactiver le compte' : 'Activer le compte'}
                </Button>
                <Button
                  variant="outline"
                  disabled={!account.active || isGenerating || !canEdit}
                  onClick={() => generateQrcode({})}
                >
                  <QrCode className="size-4" />
                  Générer un QR code
                </Button>
              </div>

              {qrcode && (
                <div className="flex flex-col items-center gap-2 border rounded-md p-4">
                  <img src={qrcode.image} alt="QR code du compte test" className="w-64 h-64" />
                  {qrcode.expiresAt && (
                    <p className="text-sm text-muted-foreground">
                      Valable jusqu'à {DateTime.fromISO(qrcode.expiresAt).toFormat('HH:mm')}
                    </p>
                  )}
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

export const Route = createFileRoute('/_authenticated/staging-tools')({
  staticData: {
    permission: STAGING_TOOLS_READ,
  },
  component: StagingToolsPage,
})
withRoutePermissions(Route)
